import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowRight, Calendar, ShieldCheck, Sparkles, Clock, MapPin, Wifi, Monitor, Armchair, Snowflake, Plug, Printer, Coffee, PenTool, ParkingSquare, Footprints, Sparkle } from "lucide-react";
import api from "@/lib/api";

const INCLUDED_FEATURES = [
    { icon: Wifi, title: "High-speed WiFi", body: "Reliable, business-grade fibre — strong enough for video calls, large file uploads, and tethering." },
    { icon: Monitor, title: "Large external monitor", body: "Plug and play! Utilise our external monitors for more desktop real estate." },
    { icon: Armchair, title: "Ergonomic desk + chair", body: "Built for full-day comfort. Adjust, settle in, get the work done." },
    { icon: Snowflake, title: "Air-conditioned workspace", body: "Cool, quiet and consistent — regardless of what the Caribbean sun is doing outside." },
    { icon: Plug, title: "Power for every device", body: "Additional outlets so your laptop, phone, headphones and tablet all stay charged." },
    { icon: Printer, title: "Free printing (up to 20 pages)", body: "Last-minute contract? Boarding pass? Print on the spot — included with every booking." },
    { icon: Coffee, title: "Complimentary refreshments", body: "Water, tea and coffee are on the house." },
    { icon: PenTool, title: "Free basic stationery", body: "Pens, pencils, markers, erasers, envelopes, highlighters, staplers — everything you forgot at home." },
    { icon: ParkingSquare, title: "Ample, safe parking", body: "Park easily on-site. No meters, no hunting for a spot, no surprises on the way out." },
    { icon: Footprints, title: "Walking distance to amenities", body: "Banks, malls, restaurants and transportation hubs — all within a short walk." },
];

export default function Landing() {
    const [pkg, setPkg] = useState({ half_day: { price_usd: 35 }, full_day: { price_usd: 60 } });
    const [includedOpen, setIncludedOpen] = useState(false);
    useEffect(() => {
        api.get("/settings/public").then((r) => setPkg(r.data.packages || pkg)).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (
        <div className="min-h-screen bg-white">
            {/* Top bar */}
            <header className="px-6 md:px-12 lg:px-16 h-16 flex items-center justify-between border-b border-slate-200/60 sticky top-0 z-40 bg-white/85 backdrop-blur-xl">
                <div className="flex items-center gap-2.5">
                    <div className="h-8 w-8 bg-brand text-white grid place-items-center font-heading font-bold tracking-tight rounded-sm">
                        A
                    </div>
                    <div className="leading-tight">
                        <div className="font-heading font-bold text-slate-900 text-base">Allied Pod</div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono">Barbados</div>
                    </div>
                </div>
                <nav className="hidden md:flex items-center gap-8 text-sm text-slate-600">
                    <NavLink href="#how" testid="nav-how">How it works</NavLink>
                    <NavLink href="#included" testid="nav-included">What's Included</NavLink>
                    <NavLink href="#pricing" testid="nav-pricing">Pricing</NavLink>
                    <NavLink href="#faq" testid="nav-faq">FAQ</NavLink>
                    <Link to="/terms" data-testid="nav-terms" className="relative inline-block py-1 transition-colors duration-200 hover:text-slate-900 after:absolute after:left-0 after:-bottom-0.5 after:h-px after:w-0 after:bg-brand after:transition-all after:duration-300 hover:after:w-full">
                        Terms
                    </Link>
                </nav>
                <div className="flex items-center gap-2">
                    <Link to="/login">
                        <Button variant="ghost" size="sm" data-testid="landing-login-btn" className="text-slate-700">
                            Sign in
                        </Button>
                    </Link>
                    <Link to="/register">
                        <Button size="sm" className="bg-brand hover:bg-brand-hover text-white rounded-md" data-testid="landing-register-btn">
                            Create account
                        </Button>
                    </Link>
                </div>
            </header>

            {/* Hero */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 bg-grid-soft pointer-events-none" />
                <div className="px-6 md:px-12 lg:px-16 pt-20 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center relative">
                    <div className="lg:col-span-6 space-y-7">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border border-slate-200 bg-white shadow-sm">
                            <span className="h-1.5 w-1.5 bg-brand rounded-full animate-pulse" />
                            <span className="text-[11px] uppercase tracking-[0.2em] font-mono text-slate-700">Now booking — Bridgetown, Barbados</span>
                        </div>
                        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-bold text-slate-900 leading-[1.05]">
                            Quiet, professional pods —
                            <br />
                            <span className="text-brand">booked by the half-day.</span>
                        </h1>
                        <p className="text-base text-slate-600 leading-relaxed max-w-xl">
                            Allied Consulting offers a private office pod in central, professional locations in Barbados for consultants, remote workers, visiting executives, and students who need focused time to study. Reserve a session in seconds with our credit-based system — no contracts, no surprises.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3 pt-2">
                            <Link to="/register">
                                <Button size="lg" data-testid="hero-cta-btn" className="bg-brand hover:bg-brand-hover text-white rounded-md h-12 px-6 group">
                                    Get started
                                    <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
                                </Button>
                            </Link>
                            <a href="#included">
                                <Button size="lg" variant="outline" data-testid="hero-included-btn" className="rounded-md h-12 px-6 border-slate-300">
                                    See what's included
                                </Button>
                            </a>
                        </div>
                        <div className="grid grid-cols-3 gap-4 pt-6 border-t border-slate-200/60 max-w-md">
                            <Stat label="Sessions" value="Mon–Fri" icon={Calendar} />
                            <Stat label="Slot length" value="4 hours" icon={Clock} />
                            <Stat label="Booking window" value="14 days" icon={Sparkles} />
                        </div>
                    </div>
                    <div className="lg:col-span-6 relative">
                        <div className="aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5] w-full overflow-hidden rounded-md border border-slate-200 shadow-[0_24px_60px_-20px_rgba(0,47,167,0.25)]">
                            <img
                                src="https://images.unsplash.com/photo-1765366417060-7d87b2e854a0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvZmZpY2UlMjBwb2R8ZW58MHx8fHwxNzc4MDExOTUwfDA&ixlib=rb-4.1.0&q=85"
                                alt="Modern office pods"
                                className="h-full w-full object-cover"
                            />
                        </div>
                        <div className="absolute -bottom-5 -left-5 bg-white border border-slate-200 rounded-sm p-4 shadow-md hidden sm:block">
                            <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-slate-500">Today's availability</div>
                            <div className="mt-1 flex items-center gap-3">
                                <div className="flex items-center gap-1.5 text-sm font-medium text-slate-900">
                                    <span className="h-2 w-2 bg-brand rounded-full" /> Morning
                                </div>
                                <div className="flex items-center gap-1.5 text-sm font-medium text-slate-400">
                                    <span className="h-2 w-2 bg-slate-300 rounded-full" /> Afternoon
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* What's Included */}
            <section id="included" className="px-6 md:px-12 lg:px-16 py-24 border-t border-slate-200/60 bg-white">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-4">
                        <p className="text-[11px] uppercase tracking-[0.2em] font-mono text-slate-500 mb-3">What's Included</p>
                        <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-bold text-slate-900 leading-tight">
                            Walk in. Plug in.<br/>Get to work — or study.
                        </h2>
                        <p className="text-base text-slate-600 mt-4 leading-relaxed">
                            Every booking comes with the things you'd otherwise have to pack, queue for, or pay extra for elsewhere.
                            Nothing is upsold. Nothing is metered. Quiet enough for exam prep, thesis writing, and long client calls alike.
                        </p>
                        <Button
                            onClick={() => setIncludedOpen(true)}
                            data-testid="included-open-dialog-btn"
                            className="mt-7 bg-slate-900 hover:bg-slate-800 text-white rounded-md group"
                        >
                            Quick view
                            <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
                        </Button>
                    </div>
                    <div className="lg:col-span-8">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
                            {INCLUDED_FEATURES.map((f, i) => (
                                <IncludedItem key={f.title} index={i} {...f} />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* How it works */}
            <section id="how" className="px-6 md:px-12 lg:px-16 py-24 border-t border-slate-200/60 bg-slate-50/50">
                <div className="max-w-2xl">
                    <p className="text-[11px] uppercase tracking-[0.2em] font-mono text-slate-500 mb-3">How it works</p>
                    <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-bold text-slate-900">
                        Three steps. No friction.
                    </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
                    <Step n="01" title="Buy credits" body="Pay via PayPal — credits are credited to your account once payment is confirmed." />
                    <Step n="02" title="Pick a slot" body="Choose any open Morning or Afternoon session up to 14 days in advance." />
                    <Step n="03" title="Show up & work" body="Arrive at your booked time. The pod is yours for a full 4-hour session." />
                </div>
            </section>

            {/* Pricing */}
            <section id="pricing" className="px-6 md:px-12 lg:px-16 py-24">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 max-w-5xl">
                    <div className="max-w-2xl">
                        <p className="text-[11px] uppercase tracking-[0.2em] font-mono text-slate-500 mb-3">Pricing</p>
                        <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-bold text-slate-900">Pay only for the time you use.</h2>
                        <p className="text-base text-slate-600 mt-4 leading-relaxed">All credits expire 45 days after purchase. Cancel up to 24 hours before your session for a full credit refund.</p>
                    </div>
                    <Button
                        onClick={() => setIncludedOpen(true)}
                        data-testid="pricing-included-btn"
                        variant="outline"
                        className="rounded-md border-slate-300 hover:border-brand hover:text-brand transition-colors group w-fit"
                    >
                        <Sparkle className="h-4 w-4 mr-2" strokeWidth={1.75} />
                        See what's included
                    </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12 max-w-4xl">
                    <PriceCard
                        title="Half Day"
                        price={`$${pkg.half_day?.price_usd ?? 35}`}
                        unit="USD / 1 credit"
                        features={["4-hour session", "Morning OR Afternoon", "45-day credit validity", "Free cancel ≥24h"]}
                        testid="pricing-half-day"
                    />
                    <PriceCard
                        title="Full Day"
                        price={`$${pkg.full_day?.price_usd ?? 60}`}
                        unit="USD / 2 credits"
                        features={["8-hour session (8a–5p)", "Morning + Afternoon", `Save $${(((pkg.half_day?.price_usd ?? 35) * 2) - (pkg.full_day?.price_usd ?? 60)).toFixed(0)} vs two halves`, "Same cancellation terms"]}
                        highlighted
                        testid="pricing-full-day"
                    />
                </div>
            </section>

            {/* Trust strip */}
            <section className="px-6 md:px-12 lg:px-16 py-20 border-t border-slate-200/60 bg-white">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl">
                    <Trust icon={ShieldCheck} title="Privacy first" body="Your bookings are never visible to other users. Only Available / Booked is shown publicly." />
                    <Trust icon={MapPin} title="Central Location" body="Walking distance to client offices, banks, malls and the transportation hubs." />
                    <Trust icon={Clock} title="Strict 4-hour slots" body="No overnight stays, no overlap. Predictable, professional, on-time." />
                    <Trust icon={Sparkles} title="Your pod, every time" body="Same quiet space on every visit. No set-up friction — just sit, settle, and get back to deep work or study." />
                </div>
            </section>

            {/* FAQ */}
            <section id="faq" className="px-6 md:px-12 lg:px-16 py-24 border-t border-slate-200/60 bg-slate-50/50">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                    <div className="lg:col-span-1">
                        <p className="text-[11px] uppercase tracking-[0.2em] font-mono text-slate-500 mb-3">Questions</p>
                        <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-bold text-slate-900">Quick answers.</h2>
                    </div>
                    <div className="lg:col-span-2 space-y-6">
                        <FAQ q="I'm a student — is this for me?" a="Absolutely. If you need a quiet, distraction-free place to prep for exams, write a thesis, or just escape the noise of campus, our pods are ideal. Many of our regulars book the same half-day slot each week and build their study routine around it." />
                        <FAQ q="Can I book on a public holiday?" a="No. Each office booking is followed by a general clean, and bookings are disallowed on any day our team will not be in to clean." />
                        <FAQ q="What if I cancel late or don't show up?" a="The first occurrence is waived as a courtesy. After that, the credits used are forfeited." />
                        <FAQ q="What happens if I overrun my session?" a="A 15–20 minute grace period applies. Beyond that, an admin may deduct 1 additional credit at their discretion." />
                        <FAQ q="How do refunds work?" a="In cases of no breach of terms, an Admin will issue credit refunds case-by-case upon receipt of the refund requests." />
                        <FAQ q="What's actually included in the price?" a="Plenty — see the full list on the What's Included section above. Short version: WiFi, dual monitor, ergonomic chair, AC, refreshments, free printing, stationery, parking, and a walkable neighbourhood." />
                    </div>
                </div>
            </section>

            <footer className="border-t border-slate-200/60 px-6 md:px-12 lg:px-16 py-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div className="text-xs text-slate-500">© {new Date().getFullYear()} Allied Consulting (Barbados). All rights reserved.</div>
                <div className="flex items-center gap-4">
                    <a href="#included" className="text-xs text-slate-600 hover:text-slate-900 transition-colors">What's Included</a>
                    <Link to="/terms" data-testid="footer-terms-link" className="text-xs text-slate-600 hover:text-slate-900 transition-colors">Terms & Licence</Link>
                    <Link to="/login">
                        <Button variant="ghost" size="sm" className="text-slate-700">Sign in</Button>
                    </Link>
                    <Link to="/register">
                        <Button size="sm" className="bg-brand hover:bg-brand-hover text-white rounded-md">Create account</Button>
                    </Link>
                </div>
            </footer>

            {/* Included features dialog (used by Hero CTA + Pricing button) */}
            <Dialog open={includedOpen} onOpenChange={setIncludedOpen}>
                <DialogContent className="max-w-2xl rounded-md border-slate-200" data-testid="included-dialog">
                    <DialogHeader>
                        <p className="text-[11px] uppercase tracking-[0.2em] font-mono text-slate-500">What's Included</p>
                        <DialogTitle className="font-heading text-2xl tracking-tight">Every booking, fully equipped.</DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 mt-2 max-h-[60vh] overflow-y-auto pr-1">
                        {INCLUDED_FEATURES.map((f) => {
                            const Icon = f.icon;
                            return (
                                <div key={f.title} className="flex items-start gap-3">
                                    <div className="h-9 w-9 shrink-0 bg-brand/10 text-brand grid place-items-center rounded-sm">
                                        <Icon className="h-4 w-4" strokeWidth={1.75} />
                                    </div>
                                    <div>
                                        <p className="font-heading font-semibold text-slate-900 text-sm">{f.title}</p>
                                        <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">{f.body}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Stat({ label, value, icon: Icon }) {
    return (
        <div>
            <div className="flex items-center gap-1.5 text-slate-500">
                <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
                <span className="text-[10px] uppercase tracking-[0.18em] font-mono">{label}</span>
            </div>
            <div className="mt-1 font-heading font-bold text-slate-900 text-base">{value}</div>
        </div>
    );
}

function Step({ n, title, body }) {
    return (
        <div className="bg-white border border-slate-200 rounded-md p-7 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all duration-300">
            <div className="font-mono text-xs text-brand tracking-[0.2em]">{n}</div>
            <h3 className="font-heading text-xl font-bold text-slate-900 mt-3">{title}</h3>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">{body}</p>
        </div>
    );
}

function PriceCard({ title, price, unit, features, highlighted, testid }) {
    return (
        <div
            data-testid={testid}
            className={`relative rounded-md p-8 transition-all duration-300 ${
                highlighted
                    ? "bg-slate-900 text-white border border-slate-900 shadow-xl"
                    : "bg-white border border-slate-200 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
            }`}
        >
            {highlighted && (
                <div className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.2em] font-mono bg-brand text-white px-2 py-1 rounded-sm">Best value</div>
            )}
            <div className={`text-[11px] uppercase tracking-[0.2em] font-mono ${highlighted ? "text-slate-300" : "text-slate-500"}`}>{title}</div>
            <div className="mt-4 flex items-baseline gap-2">
                <span className="font-heading text-5xl font-bold tracking-tighter">{price}</span>
                <span className={`text-sm ${highlighted ? "text-slate-300" : "text-slate-500"}`}>{unit}</span>
            </div>
            <ul className={`mt-6 space-y-2.5 text-sm ${highlighted ? "text-slate-200" : "text-slate-700"}`}>
                {features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5">
                        <span className={`h-1 w-1 rounded-full mt-2 ${highlighted ? "bg-brand" : "bg-slate-900"}`} />
                        {f}
                    </li>
                ))}
            </ul>
            <Link to="/register" className="block mt-7">
                <Button className={`w-full rounded-md h-11 ${highlighted ? "bg-brand hover:bg-brand-hover text-white" : "bg-slate-900 hover:bg-slate-800 text-white"}`}>
                    Get started
                </Button>
            </Link>
        </div>
    );
}

function Trust({ icon: Icon, title, body }) {
    return (
        <div className="flex gap-4">
            <div className="h-10 w-10 shrink-0 bg-brand/10 text-brand grid place-items-center rounded-sm">
                <Icon className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <div>
                <h3 className="font-heading text-base font-bold text-slate-900">{title}</h3>
                <p className="text-sm text-slate-600 mt-1 leading-relaxed">{body}</p>
            </div>
        </div>
    );
}

function FAQ({ q, a }) {
    return (
        <div className="border-b border-slate-200 pb-6 last:border-0">
            <h3 className="font-heading text-lg font-semibold text-slate-900">{q}</h3>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">{a}</p>
        </div>
    );
}

function NavLink({ href, testid, children }) {
    return (
        <a
            href={href}
            data-testid={testid}
            className="relative inline-block py-1 transition-colors duration-200 hover:text-slate-900 after:absolute after:left-0 after:-bottom-0.5 after:h-px after:w-0 after:bg-brand after:transition-all after:duration-300 hover:after:w-full"
        >
            {children}
        </a>
    );
}

function IncludedItem({ icon: Icon, title, body, index }) {
    return (
        <div
            className="flex items-start gap-4 py-5 border-b border-slate-100 last:border-0 group"
            style={{ animation: `fade-up 0.5s ease-out ${index * 50}ms both` }}
        >
            <div className="h-10 w-10 shrink-0 bg-brand/5 text-brand grid place-items-center rounded-sm transition-all duration-300 group-hover:bg-brand group-hover:text-white">
                <Icon className="h-5 w-5" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
                <h3 className="font-heading text-base font-semibold text-slate-900">{title}</h3>
                <p className="text-sm text-slate-600 mt-1 leading-relaxed">{body}</p>
            </div>
        </div>
    );
}
